import React, { useState } from 'react';
import Seo from '../components/Seo';
import '../styles/AppBrief.css';

const markets = [
  {
    title: 'Forex & Commodities',
    icon: 'trend',
    items: ['Gold', 'Silver', 'EUR/USD', 'USD/JPY', 'GBP/USD', 'XAU/USD'],
  },
  {
    title: 'Crypto',
    icon: 'coin',
    items: ['Bitcoin', 'Ethereum', 'Solana', 'XRP', 'Dogecoin'],
  },
  {
    title: 'Stocks',
    icon: 'building',
    items: ['Tesla', 'Nvidia', 'Google', 'Apple', 'Amazon', 'Meta'],
  },
];

const appCapabilities = [
  'Reads charts visually from screenshots',
  'Checks numbers, lines, price movements, and patterns',
  'Performs technical analysis based on different criteria',
  'Searches Google automatically for relevant context',
  'Reads articles and market information',
  'Summarizes findings into simple trading insights',
];

const audienceTraits = [
  'Interested in active trading and day trading',
  'Curious about forex, crypto, and stocks',
  'Smart and critical, but not highly experienced traders',
  'Ambitious and motivated by financial freedom',
  'Dreamers who want a better lifestyle',
  'Middle class or above',
  'Based in Europe, North America, the Middle East, Southeast Asia, or Africa',
];

const videoAngles = [
  'Showing how Chartmaxx AI works',
  'Uploading a TradingView or MetaTrader chart into the app',
  'Showing analysis for XAU/USD, BTC, Tesla, Nvidia, and similar assets',
  'Comparing manual chart analysis vs AI chart analysis',
  'Positioning Chartmaxx AI next to TradingView or MetaTrader',
  'Showing how AI can simplify trading decisions',
  'Creating day-in-the-life trader clips',
  'Posting trading journey updates using Chartmaxx AI',
];

const usernameExamples = [
  'JoeFX',
  'TraderJoe',
  'JoeTrades',
  'FXJourney',
  'CryptoJoe',
  'XAUTrader',
  'AITraderJoe',
  'TradeWithJoe',
];

const bioExamples = [
  'Documenting my trading journey',
  'Escaping the 9-5',
  'Follow my trading journey',
  'Crypto & Forex degen',
  'XAU/USD trader',
  'Learning forex with AI',
  'Using Chartmaxx AI to trade smarter',
  'Making money with AI trading',
  'Motivation, trading & financial freedom',
  'Testing AI tools for trading',
];

const contentIdeas = [
  {
    title: 'App Demo',
    icon: 'screen',
    body: 'Show a TradingView or MetaTrader chart, take a screenshot, upload it to Chartmaxx AI, and show the result.',
    hook: 'I uploaded my XAU/USD chart into an AI trading app and this is what it said...',
  },
  {
    title: 'AI vs Human Analysis',
    icon: 'compare',
    body: 'Show your own basic analysis, then compare it with Chartmaxx AI.',
    hook: 'I thought gold was going down... then AI showed me this.',
  },
  {
    title: 'Beginner Trader Angle',
    icon: 'spark',
    body: 'Make content for people who feel confused by charts.',
    hook: 'If trading charts look confusing, this app explains them in seconds.',
  },
  {
    title: 'Lifestyle / Motivation Angle',
    icon: 'bolt',
    body: 'Combine trading clips with luxury visuals, cars, watches, city views, laptops, charts, and motivational text.',
    hook: 'Learning trading in 2026 feels different with AI.',
  },
  {
    title: 'Trending Asset Angle',
    icon: 'target',
    body: 'Use assets people are already talking about, such as gold, Bitcoin, Solana, Tesla, Nvidia, EUR/USD, and Ethereum.',
    hook: 'I asked AI to analyze Bitcoin before my next trade.',
  },
];

const hashtags = [
  '#xauusd',
  '#forex',
  '#forextrading',
  '#crypto',
  '#cryptotrading',
  '#stocks',
  '#stocktrading',
  '#aitrading',
  '#ai',
  '#btc',
  '#bitcoin',
  '#ethereum',
  '#tradingview',
  '#metatrader',
  '#daytrading',
  '#traderjourney',
  '#financialfreedom',
];

const inspirationAccounts = [
  {
    name: 'chart.ai.app',
    instagram: 'https://www.instagram.com/chart.ai.app/',
    tiktok: 'https://www.tiktok.com/@chart.ai.app',
  },
  {
    name: 'try_chartdetector',
    instagram: 'https://www.instagram.com/try_chartdetector/',
    tiktok: 'https://www.tiktok.com/@try_chartdetector',
  },
];

function Icon({ name }) {
  const icons = {
    analysis: (
      <>
        <path d="M4 18V6" />
        <path d="M10 18V9" />
        <path d="M16 18V4" />
        <path d="M3 18h15" />
      </>
    ),
    bolt: <path d="M11 2 4 12h6l-1 8 7-11h-6l1-7Z" />,
    building: (
      <>
        <path d="M5 18V4h10v14" />
        <path d="M8 7h1" />
        <path d="M12 7h1" />
        <path d="M8 11h1" />
        <path d="M12 11h1" />
        <path d="M3 18h14" />
      </>
    ),
    coin: (
      <>
        <circle cx="10" cy="10" r="7" />
        <path d="M10 6v8" />
        <path d="M7.5 8h4a2 2 0 0 1 0 4h-4" />
      </>
    ),
    compare: (
      <>
        <path d="M4 6h9" />
        <path d="m10 3 3 3-3 3" />
        <path d="M16 14H7" />
        <path d="m10 11-3 3 3 3" />
      </>
    ),
    growth: (
      <>
        <path d="M3 15 8 10l3 3 6-8" />
        <path d="M13 5h4v4" />
      </>
    ),
    link: (
      <>
        <path d="M8 11a3 3 0 0 1 0-4l2-2a3 3 0 0 1 4 4l-1 1" />
        <path d="M12 9a3 3 0 0 1 0 4l-2 2a3 3 0 0 1-4-4l1-1" />
      </>
    ),
    markets: (
      <>
        <path d="M3 15h3l2-8 4 11 2-7h3" />
        <path d="M3 4h14" />
      </>
    ),
    screen: (
      <>
        <rect x="3" y="4" width="14" height="10" rx="2" />
        <path d="M8 18h4" />
        <path d="M10 14v4" />
      </>
    ),
    spark: (
      <>
        <path d="M10 2 8.5 7.5 3 9l5.5 1.5L10 16l1.5-5.5L17 9l-5.5-1.5L10 2Z" />
        <path d="M15 14v4" />
        <path d="M13 16h4" />
      </>
    ),
    target: (
      <>
        <circle cx="10" cy="10" r="7" />
        <circle cx="10" cy="10" r="3" />
        <path d="M10 10h7" />
      </>
    ),
    trend: (
      <>
        <path d="M3 14 8 9l3 3 6-7" />
        <path d="M13 5h4v4" />
      </>
    ),
    upload: (
      <>
        <path d="M10 13V3" />
        <path d="m6 7 4-4 4 4" />
        <path d="M4 15v2h12v-2" />
      </>
    ),
    user: (
      <>
        <circle cx="10" cy="7" r="3" />
        <path d="M4 17a6 6 0 0 1 12 0" />
      </>
    ),
    video: (
      <>
        <rect x="3" y="5" width="10" height="10" rx="2" />
        <path d="m13 9 4-3v8l-4-3" />
      </>
    ),
  };

  return (
    <svg className="brief-icon" viewBox="0 0 20 20" aria-hidden="true">
      {icons[name]}
    </svg>
  );
}

function AppBrief() {
  const [isPlatformModalOpen, setIsPlatformModalOpen] = useState(false);

  return (
    <main className="app-brief-page">
      <Seo
        title="Chartmaxx AI App Brief | UGC, Trading Content & AI Chart Analysis"
        description="Read the Chartmaxx AI app brief for UGC creators and clipper accounts, including target audience, workflow, content ideas, hashtags, and links for AI trading chart analysis."
        path="/app-brief"
      />
      <section className="brief-hero">
        <div>
          <p className="brief-kicker">Chartmaxx AI</p>
          <h1>App Brief</h1>
          <p>
            Chartmaxx AI helps users understand market charts faster by combining AI chart analysis,
            market research, technical analysis, and simplified trading insights.
          </p>
          <a className="brief-primary-link" href="https://www.tiktok.com/@chartmaxx" target="_blank" rel="noreferrer">
            Main TikTok account: @chartmaxx
          </a>
        </div>
        <aside className="brief-summary">
          <span>Key message</span>
          <p>
            Made for people who want to trade smarter, save time, and make more informed decisions
            in forex, crypto, stocks, commodities, and indices.
          </p>
        </aside>
      </section>

      <section className="brief-section two-column">
        <article className="brief-card">
          <Icon name="upload" />
          <p className="brief-kicker">Workflow</p>
          <h2>How the app works</h2>
          <ol className="brief-steps">
            <li>Open a chart from TradingView, MetaTrader 5, or another charting platform.</li>
            <li>Take a screenshot of any financial instrument.</li>
            <li>Upload the screenshot to Chartmaxx AI.</li>
            <li>Review simplified insights based on chart analysis and market research.</li>
          </ol>
          <button
            className="brief-info-button"
            type="button"
            onClick={() => setIsPlatformModalOpen(true)}
          >
            More information
          </button>
        </article>
        <article className="brief-card">
          <Icon name="analysis" />
          <p className="brief-kicker">Analysis</p>
          <h2>What Chartmaxx AI can do</h2>
          <ul className="brief-list">
            {appCapabilities.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        </article>
      </section>

      <section className="brief-section">
        <div className="section-heading">
          <div className="heading-kicker-row">
            <Icon name="markets" />
            <p className="brief-kicker">Markets</p>
          </div>
          <h2>Examples of supported financial instruments</h2>
          <p>
            These are example markets Chartmaxx AI can analyze. Users can upload charts from many
            other forex pairs, cryptocurrencies, stocks, commodities, and indices as well.
          </p>
        </div>
        <div className="market-grid">
          {markets.map((market) => (
            <article className="brief-card" key={market.title}>
              <div className="card-heading">
                <Icon name={market.icon} />
                <h3>{market.title}</h3>
              </div>
              <div className="tag-list">
                {market.items.map((item) => (
                  <span key={item}>{item}</span>
                ))}
              </div>
            </article>
          ))}
        </div>
      </section>

      <section className="brief-section">
        <div className="section-heading">
          <div className="heading-kicker-row">
            <Icon name="user" />
            <p className="brief-kicker">Audience</p>
          </div>
          <h2>Who uses the app?</h2>
          <p>
            The main target audience is men aged 18-35 who follow trading, crypto, forex,
            investing, financial freedom, luxury lifestyle, cars, football, motivation, business,
            and escape-the-9-5 content.
          </p>
        </div>
        <div className="trait-grid">
          {audienceTraits.map((trait) => (
            <span key={trait}>{trait}</span>
          ))}
        </div>
      </section>

      <section className="brief-section two-column">
        <article className="brief-card accent-card">
          <Icon name="growth" />
          <p className="brief-kicker">Growth</p>
          <h2>How to reach users and get views</h2>
          <p>
            The strongest format is short-form video that blends trading, AI, lifestyle, and
            motivation. Use trending sounds from cars, football, luxury lifestyle, crypto, forex,
            motivation, business, and escape-the-9-5 content.
          </p>
        </article>
        <article className="brief-card">
          <Icon name="video" />
          <h2>Strong video angles</h2>
          <ul className="brief-list compact">
            {videoAngles.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        </article>
      </section>

      <section className="brief-section">
        <div className="section-heading">
          <div className="heading-kicker-row">
            <Icon name="user" />
            <p className="brief-kicker">UGC / Clipper Tips</p>
          </div>
          <h2>Make your account feel authentic</h2>
          <p>
            Accounts should look like they belong in the trading, forex, crypto, or financial
            freedom space. Include words like FX, Trades, Trader, or Crypto in the username.
          </p>
        </div>
        <div className="two-column">
          <article className="brief-card">
            <div className="card-heading">
              <Icon name="target" />
              <h3>Username examples</h3>
            </div>
            <div className="tag-list">
              {usernameExamples.map((name) => (
                <span key={name}>{name}</span>
              ))}
            </div>
          </article>
          <article className="brief-card">
            <div className="card-heading">
              <Icon name="spark" />
              <h3>Bio examples</h3>
            </div>
            <ul className="brief-list compact">
              {bioExamples.map((bio) => (
                <li key={bio}>{bio}</li>
              ))}
            </ul>
          </article>
        </div>
        <p className="brief-note">
          Important: the account should feel like someone is documenting their own trading journey,
          not just posting ads.
        </p>
      </section>

      <section className="brief-section">
        <div className="section-heading">
          <div className="heading-kicker-row">
            <Icon name="video" />
            <p className="brief-kicker">Content Ideas</p>
          </div>
          <h2>Short-form formats to test</h2>
        </div>
        <div className="idea-grid">
          {contentIdeas.map((idea, index) => (
            <article className="brief-card idea-card" key={idea.title}>
              <div className="idea-topline">
                <span className="idea-number">{index + 1}</span>
                <Icon name={idea.icon} />
              </div>
              <h3>{idea.title}</h3>
              <p>{idea.body}</p>
              <blockquote>{idea.hook}</blockquote>
            </article>
          ))}
        </div>
      </section>

      <section className="brief-section two-column">
        <article className="brief-card">
          <Icon name="spark" />
          <p className="brief-kicker">Inspiration</p>
          <h2>Competitors to study</h2>
          <p>
            Study these accounts for video formats, hooks, editing style, captions, and comment
            sections. Use them as inspiration, without copying directly.
          </p>
          <div className="account-link-grid">
            {inspirationAccounts.map((account) => (
              <div className="account-link-row" key={account.name}>
                <span>@{account.name}</span>
                <div>
                  <a href={account.tiktok} target="_blank" rel="noreferrer">TikTok</a>
                  <a href={account.instagram} target="_blank" rel="noreferrer">Instagram</a>
                </div>
              </div>
            ))}
          </div>
        </article>
        <article className="brief-card">
          <Icon name="target" />
          <p className="brief-kicker">Hashtags</p>
          <h2>Suggested hashtag mix</h2>
          <div className="tag-list">
            {hashtags.map((hashtag) => (
              <span key={hashtag}>{hashtag}</span>
            ))}
          </div>
        </article>
      </section>

      <section className="brief-section final-callout">
        <Icon name="link" />
        <p className="brief-kicker">Important Requirement</p>
        <h2>Every UGC or clipper account must include this URL in the bio</h2>
        <a href="https://apps.apple.com/us/app/chartmaxx-ai/id6740340875" target="_blank" rel="noreferrer">
          https://apps.apple.com/us/app/chartmaxx-ai/id6740340875
        </a>
      </section>

      <section className="brief-section contact-callout">
        <p className="brief-kicker">Interested?</p>
        <h2>Contact Tommy to get started</h2>
        <a href="mailto:tommy@hubbie.ai">Contact tommy@hubbie.ai</a>
      </section>

      {isPlatformModalOpen && (
        <div
          className="brief-modal-backdrop"
          role="presentation"
          onClick={() => setIsPlatformModalOpen(false)}
        >
          <div
            className="brief-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="platform-modal-title"
            onClick={(event) => event.stopPropagation()}
          >
            <button
              className="brief-modal-close"
              type="button"
              aria-label="Close platform information"
              onClick={() => setIsPlatformModalOpen(false)}
            >
              X
            </button>
            <p className="brief-kicker">Chart platforms</p>
            <h2 id="platform-modal-title">TradingView and MetaTrader 5</h2>
            <div className="platform-info-grid">
              <article>
                <Icon name="screen" />
                <h3>TradingView</h3>
                <p>
                  TradingView is a web-based charting platform used to view markets, draw on charts,
                  add indicators, and follow price movement across assets like forex, crypto,
                  stocks, commodities, and indices.
                </p>
                <a href="https://www.tradingview.com/" target="_blank" rel="noreferrer">
                  Open TradingView
                </a>
              </article>
              <article>
                <Icon name="analysis" />
                <h3>MetaTrader 5</h3>
                <p>
                  MetaTrader 5 is a trading platform used by many forex and CFD traders. Users can
                  open broker charts, add indicators, monitor price movement, and take screenshots
                  for Chartmaxx AI analysis.
                </p>
                <a href="https://www.metatrader5.com/en" target="_blank" rel="noreferrer">
                  Open MetaTrader 5
                </a>
              </article>
            </div>
          </div>
        </div>
      )}
    </main>
  );
}

export default AppBrief;
